"use client";

import { FC, SyntheticEvent, memo, useCallback, useContext, useMemo } from "react";
import { IMarketOutcomeFeedResponse } from "@finbackoffice/clientbff-client";
import { IEventFeedResponse } from "@finbackoffice/websocket-client";
import {
    SportFavoritesContext,
    useMainMarketOdds,
    useRuntimeConfig,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import { AssetsDirectory } from "@finbackoffice/enums";
import Link from "next/link";
import classNames from "classnames";
import MainMarket from "components/sport/base/main-market/MainMarket";
import { Svg } from "components/base/svg/Svg";
import { useBetSlipOutcomeHandler, useSportFeedType } from "hooks";
import TopDefault from "./scoreboard/TopDefault";
import TopInternational from "./scoreboard/TopInternational";
import styles from "./home-sport-game-top.module.sass";

type Props = {
    game: IEventFeedResponse;
    index: number;
    onGameClickHandler: (gameId: number) => void;
};

const HomeSportGameTop: FC<Props> = ({ game, index, onGameClickHandler }) => {
    const ASSETS_URL = useRuntimeConfig("ASSETS_URL");
    const { query } = useSearchParamsQuery();
    const { isBCFeed } = useSportFeedType();
    const { isFav, handleFavClick } = useContext(SportFavoritesContext);

    const { mainOdds, mainMarketName, mainMarketStatus, mainMarketId, columnCount } =
        useMainMarketOdds(
            game.sport_id,
            game.markets,
            isBCFeed,
            game.home_team?.name || "",
            game.away_team?.name || "",
        );
    const isInternationalCategory = game.category_id === 4;

    const handleOutcomeClick = useBetSlipOutcomeHandler({
        gameId: game.id,
        homeName: game.home_team?.name || "",
        awayName: game.away_team?.name || "",
        gameStatus: game.status,
    });

    const onOutcomeClick = useCallback(
        (outcome: IMarketOutcomeFeedResponse | null) => {
            handleOutcomeClick(mainMarketId, outcome, mainMarketName, mainMarketStatus);
        },
        [handleOutcomeClick, mainMarketId, mainMarketName, mainMarketStatus],
    );

    const handleStarClick = useCallback(
        (event: SyntheticEvent) => {
            event.preventDefault();
            event.stopPropagation();

            handleFavClick(game.id, game.sport_id, game.tournament_id, game.scheduled_at || "");
        },
        [game.id, game.sport_id, game.tournament_id, game.scheduled_at, handleFavClick],
    );

    const handleGameClick = useCallback(
        () => onGameClickHandler(game.id),
        [onGameClickHandler, game.id],
    );

    const backgroundImage = useMemo(
        () =>
            isInternationalCategory
                ? `url(${ASSETS_URL}/${AssetsDirectory.Common}/${AssetsDirectory.HomeScoreboards}/${AssetsDirectory.InternationalSoccer}.webp)`
                : `url(${ASSETS_URL}/${AssetsDirectory.Common}/${isBCFeed ? AssetsDirectory.BCFeed : AssetsDirectory.BRFeed}/${AssetsDirectory.HomeScoreboards}/${game.sport_id}.webp)`,
        [ASSETS_URL, isInternationalCategory, isBCFeed, game.sport_id],
    );

    return (
        <div>
            <div className={styles.topGameItem} style={{ backgroundImage }}>
                {game.tournament_is_top && (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/top-leagues.svg`}
                        className={styles.flagTop}
                    />
                )}

                <i
                    className={classNames(styles.star, isFav(game.id) && styles.starred)}
                    onClick={handleStarClick}
                />
                <Link
                    onClick={handleGameClick}
                    href={{
                        query: {
                            ...query,
                            gameId: game.id,
                        },
                    }}
                    className={styles.scoreboardLink}>
                    {isInternationalCategory ? (
                        <TopInternational game={game} index={index} />
                    ) : (
                        <TopDefault game={game} index={index} />
                    )}
                </Link>
                <MainMarket
                    mainOdds={mainOdds}
                    columnCount={columnCount}
                    marketStatus={mainMarketStatus}
                    onOutcomeClick={onOutcomeClick}
                    wrapperClassname={styles.homeGameItemMarket}
                />
            </div>
        </div>
    );
};

export default memo(HomeSportGameTop);
